/**
 * useVoicePrompter
 *
 * Interval-driven TTS. Rules:
 *  - Interval fires every PROMPT_INTERVAL_MS (1000 ms).
 *  - Never interrupts a playing utterance — waits for synthesis to go idle.
 *  - Silent when the user is moving (keypoint jitter above threshold).
 *  - "Stand still" fires once when pose becomes armed (next idle interval).
 *
 * Usage:
 *   const { prompt } = useVoicePrompter();
 *   prompt(errors, armed, moving) — call on every pose-checker tick
 *
 * Returns: { currentText: string | null }  — for on-screen caption.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { getVoiceMessage, PoseError, VoiceMessageId } from "../PoseErrorClassifier";

const PROMPT_INTERVAL_MS       = 1000;
const STAND_STILL_TEXT         = "Stand still";
const STAND_STILL_COOLDOWN_MS  = 4000; // min gap between consecutive "Stand still" utterances
const STEP_BACK_TEXT           = "Step back from the camera";
const STEP_BACK_COOLDOWN_MS    = 4000; // min gap between consecutive step-back utterances

const CAPTURE_BLOCKING_HINTS = new Set([
  STEP_BACK_TEXT,
  "Turn to face left",
  "Turn to face right",
  "Turn your back to the camera",
  "Place the phone at waist height",
  "Turn your phone to portrait",
]);

const PREFERRED_VOICE_NAME = "Samantha";

// Cache the resolved voice so getVoices() isn't called every utterance.
let cachedVoice: SpeechSynthesisVoice | null | undefined = undefined;

function getPreferredVoice(): SpeechSynthesisVoice | null {
  if (cachedVoice !== undefined) return cachedVoice;
  const voices = window.speechSynthesis.getVoices();
  voices.forEach((v) => {
     console.log(`Voice: ${v.name} (${v.lang})${v.default ? " [default]" : ""}`);
  });
  cachedVoice = voices.find((v) => v.name === PREFERRED_VOICE_NAME) ?? null;
  return cachedVoice;
}

// Invalidate cache when the voice list changes (async load on Chrome).
if (typeof window !== "undefined" && window.speechSynthesis) {
  window.speechSynthesis.addEventListener("voiceschanged", () => {
    cachedVoice = undefined;
  });
}

export function speak(text: string) {
      console.log("speak() called:", text);  // ← add this

  if (!window.speechSynthesis) return;
  // Chrome bug: speechSynthesis can get stuck with speaking=true while silent.
  // Calling resume() first unsticks it.
  window.speechSynthesis.resume();
  // Wait for any current utterance to finish — never overlap
  if (window.speechSynthesis.speaking) return;
  const utt = new SpeechSynthesisUtterance(text);
  utt.lang  = "en-US";
  utt.rate  = 0.7;
  utt.pitch = 1.2;
  const preferred = getPreferredVoice();
  console.log(`Using voice: ${preferred?.name ?? "default"}`);
  if (preferred) utt.voice = preferred;
  window.speechSynthesis.speak(utt);
}

export function useVoicePrompter() {
  const [currentText, setCurrentText] = useState<string | null>(null);

  const intervalRef       = useRef<ReturnType<typeof setInterval> | null>(null);
  const armedRef          = useRef(false);
  const standStillSaid    = useRef(false);
  const standStillLastMs  = useRef(0);
  const stepBackLastMs    = useRef(0);
  const latestErrors   = useRef<PoseError[]>([]);
  const latestMoving   = useRef(false);
  const latestV2Hint   = useRef<string | null>(null);

  // Interval tick — the only place TTS fires
  const tick = useCallback(() => {
    // Never overlap a playing utterance; retry next tick
    if (window.speechSynthesis?.speaking) return;

    const v2Hint = latestV2Hint.current;
    if (v2Hint && CAPTURE_BLOCKING_HINTS.has(v2Hint)) {
      const now = Date.now();
      setCurrentText(v2Hint);

      if (v2Hint === STEP_BACK_TEXT) {
        if (now - stepBackLastMs.current < STEP_BACK_COOLDOWN_MS) {
          return;
        }
        stepBackLastMs.current = now;
      }

      speak(v2Hint);
      return;
    }

    if (armedRef.current) {
      if (!standStillSaid.current && !latestMoving.current) {
        const now = Date.now();
        if (now - standStillLastMs.current >= STAND_STILL_COOLDOWN_MS) {
          standStillSaid.current = true;
          standStillLastMs.current = now;
          speak(STAND_STILL_TEXT);
          setCurrentText(STAND_STILL_TEXT);
        }
      }
      return;
    }

    // Don't prompt while user is moving
    if (latestMoving.current) return;

    // Framing / detection errors always take priority over the V2 pose hint.
    // Only fall through to the V2 hint when no high-priority message fires.
    const msg = getVoiceMessage(latestErrors.current);
    const FRAMING_PRIORITY: VoiceMessageId[] = [
      "wrong_orientation",
      "not_detected",
      "step_back",
      "move_to_centre",
      "step_closer",
      "adjust_camera",
    ];
    const isFramingMsg = msg && FRAMING_PRIORITY.includes(msg.id);

    if (!isFramingMsg && v2Hint) {
      speak(v2Hint);
      setCurrentText(v2Hint);
      return;
    }

    if (!msg) {
      setCurrentText(null);
      return;
    }

    speak(msg.text);
    setCurrentText(msg.text);
  }, []);

  // Start interval on mount
  useEffect(() => {
    intervalRef.current = setInterval(tick, PROMPT_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.speechSynthesis?.cancel();
    };
  }, [tick]);

  /**
   * Call on every pose-checker frame to update state.
   * @param errors   Latest PoseError[] from classifyPoseErrors
   * @param armed    True when pose has been stable for ≥ 0.5 s
   * @param moving   True when keypoints are moving above the jitter threshold
   * @param v2Hint   Optional V2 hint text (preferred for voice when present)
   */
  const prompt = useCallback(
    (errors: PoseError[], armed: boolean, moving: boolean, v2Hint?: string | null) => {
      latestErrors.current = errors;
      latestMoving.current = moving;
      latestV2Hint.current = v2Hint ?? null;

      const wasArmed = armedRef.current;
      armedRef.current = armed;

      if (armed && !wasArmed) {
        // Just became armed — try to speak "Stand still" at next idle moment
        standStillSaid.current = false;
      }

      if (wasArmed && !armed) {
        // Pose broke — reset so next error message plays fresh
        standStillSaid.current = false;
        setCurrentText(null);
      }
    },
    []
  );

  /** Reset state between shots (e.g. front → side transition) */
  const reset = useCallback(() => {
    armedRef.current = false;
    standStillSaid.current = false;
    standStillLastMs.current = 0;
    stepBackLastMs.current = 0;
    latestErrors.current = [];
    latestMoving.current = false;
    latestV2Hint.current = null;
    setCurrentText(null);
    window.speechSynthesis?.cancel();
  }, []);

  return { prompt, reset, currentText };
}
